import { ResponsiveLine } from "@nivo/line";
import { Box, HStack, Text } from "@reverb-ui/react";
import { TRangeDatesType } from "domain/entities/generic-type";
import moment from "moment";
import { useMemo } from "react";
import { TTrafficInsightsDataChart } from "../../../domain/entities/insights-on-traffic-response";

enum ETypeChart {
  Daily,
  Weekly,
  Monthly,
}

const customSliceTooltip = ({ slice }: any, typeChartData: ETypeChart) => {
  return (
    <Box
      position="relative"
      w="100%"
      bg="gray.50"
      p={1}
      px={3}
      boxShadow="md"
      borderRadius="4px"
    >
      <Text
        fontSize="15px"
        fontWeight="bold"
        marginBottom={1}
      >{`${getTooltipDateText(slice.points[0].data.x, typeChartData)}`}</Text>
      {slice.points.map((point: any) => (
        <HStack marginBottom={1}>
          <Box h="10px" w="10px" bg={point.serieColor} borderRadius="5px" />
          <Text fontSize="sm" minWidth={110}>{`${point.serieId}`}</Text>
          <Text fontSize="sm" fontWeight="bold">{`${point.data.y.toLocaleString(
            "en-US"
          )}`}</Text>
        </HStack>
      ))}
    </Box>
  );
};

function getTypeChartData(rangeDates: TRangeDatesType): ETypeChart {
  let startDate = moment(rangeDates.startDate);
  let endDate = moment(rangeDates.endDate);
  let difDays = endDate.diff(startDate, "days");

  if (difDays === 0) {
    return ETypeChart.Daily;
  }

  return difDays <= 6 ? ETypeChart.Weekly : ETypeChart.Monthly;
}

function getTooltipDateText(dates: any, typeChart: ETypeChart): string {
  let startDate = moment(dates[0]);
  let endDate = moment(dates[1]);

  switch (typeChart) {
    case ETypeChart.Daily:
      return `${startDate.format("MMMM Do, YYYY")}`;
    case ETypeChart.Weekly:
      return `${startDate.format("MMM Do, YY")} to ${endDate.format(
        "MMM Do, YY"
      )}`;
    case ETypeChart.Monthly:
      return `${startDate.format("MMMM YYYY")}`;

    default:
      return "";
  }
}

function datesBottonValues(
  quantityChartData: number,
  trafficChart: TTrafficInsightsDataChart[]
): Date[] {
  let skipNumber =
    quantityChartData < 7 ? 1 : Math.round(quantityChartData / 7);
  let datesBotton = [];

  for (var x = 0; x <= quantityChartData - 1; x += skipNumber) {
    datesBotton.push(trafficChart[x].rangeDates.startDate);
  }

  return datesBotton;
}

function textBottonValues(
  dates: Date[],
  typeChart: ETypeChart,
  datesBotton: Date[]
): string {
  let startDate = dates[0];

  if (!datesBotton.includes(startDate)) return "";

  if (typeChart === ETypeChart.Daily || typeChart === ETypeChart.Weekly) {
    return `${moment(startDate).format("MM/DD/YYYY")}`;
  }

  return moment(startDate).format("MMMM YYYY");
}

export type TChartProps = {
  paddingRight?: number | string;
};

export type TTrafficInsightChartProps = {
  chartData: TTrafficInsightsDataChart[];
  chatProps?: TChartProps;
};

export const TrafficInsightChart = ({
  chartData,
  chatProps,
}: TTrafficInsightChartProps) => {
  const quantityChartData = chartData.length;
  const typeChartData = getTypeChartData(chartData[0].rangeDates);
  const datesBottom = datesBottonValues(quantityChartData, chartData);

  const dataPoints = useMemo(() => {
    const data = chartData.reduce(
      (acc: any, { rangeDates, nBounce, nConverted, nRespondents }) => ({
        bounce: {
          id: "Bounce",
          color: "#F34242",
          data: [
            ...(acc?.bounce?.data || []),
            { x: [rangeDates.startDate, rangeDates.endDate], y: nBounce },
          ],
        },
        converted: {
          id: "Conversion",
          color: "#46BD6F",
          data: [
            ...(acc?.converted?.data || []),
            { x: [rangeDates.startDate, rangeDates.endDate], y: nConverted },
          ],
        },
        respondents: {
          id: "Unique visitors",
          color: "#4A5568",
          data: [
            ...(acc?.respondents?.data || []),
            { x: [rangeDates.startDate, rangeDates.endDate], y: nRespondents },
          ],
        },
      }),
      {}
    );
    return Object.values(data);
  }, [chartData]);

  return (
    <Box
      w="full"
      h="full"
      bg="white"
      id="traffic-chart-component"
      pr={chatProps?.paddingRight}
    >
      <ResponsiveLine
        animate={false}
        theme={{
          fontFamily: "Inter",
        }}
        data={dataPoints as any}
        margin={{ top: 50, right: 60, bottom: 90, left: 60 }}
        yScale={{
          type: "linear",
          min: "auto",
          max: "auto",
        }}
        xScale={{
          type: "point",
        }}
        enableSlices="x"
        sliceTooltip={(slice) => customSliceTooltip(slice, typeChartData)}
        colors={(d) => d.color}
        enablePoints={false}
        lineWidth={6}
        useMesh
        axisBottom={{
          format: (value) => {
            return textBottonValues(value, typeChartData, datesBottom);
          },
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legendOffset: -40,
        }}
        legends={[
          {
            anchor: "bottom",
            direction: "row",
            itemWidth: 150,
            itemHeight: 20,
            symbolShape: "circle",
            translateY: 70,
            translateX: 40,
          },
        ]}
      />
    </Box>
  );
};
